import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Table from '@HomaHealth/ui-component-library/lib/_components/molecules/Table';
import {
  // DiseaseSvg,
  DottedText,
  OverviewCard,
  RiskOverviewCard,
  ToggleGroup,
  valueWithText,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import risingRiskPageMessages from '../../../core-utils/messages/risingRiskPageMessages';
import properties from '../../../core-utils/properties';
import Loader from '../Loader';
import CardViewSvg from '../../atoms/Icons/CardViewsSvg';
import TableViewSvg from '../../atoms/Icons/TableViewSvg';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { useStores } from '../../../stores';
import { useNavigate } from 'react-router-dom';
import { RISING_RISK_DETAILS } from '../../../core-utils/routes';
import {
  dropDownMenuItemsAlphabets,
  dropDownMenuItemsValues,
  handleHeaderMouseEnter,
  handleHeaderMouseLeave,
} from '../../../core-utils/helper-utils/tableSortHelper';

export interface RisingRiskOverviewTabProps {
  startDate?: string;
  endDate?: string;
}

export interface RisingRiskSummaryCardsProps {
  avgRiskScore: string;
  latentRisk: string;
  chronicDiagnosed: string;
  acuteDiagnosed: string;
}

export interface DiseasesDataProps {
  diseaseName: string;
  currentMembers: string;
  riskMembers: string;
  totalCost: string;
  memberIdList: [];
}

const RisingRiskOverviewTab: React.FC<RisingRiskOverviewTabProps> = ({
  startDate,
  endDate,
}) => {
  const useStyles = makeStyles()({
    root: {
      display: 'flex',
      flexDirection: 'column',
    },
    diseasesHeadingGrid: {
      margin: '10px 0px 30px 0px',
    },
    diseasesTableHeader: {
      height: '63px',
      backgroundColor: '#F2F4FA',
    },
    leftIconActiveStyles: {
      color: '#475CB0',
      backgroundColor: '#475CB0',
    },
    rightIconActiveStyles: {
      color: '#475CB0',
      backgroundColor: '#475CB0',
    },
    overallGridStyles: {
      width: '100%',
      marginRight: '150px',
    },
    ellipses: {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      width: '300px',
      whiteSpace: 'nowrap',
      '&:hover': {
        overflow: 'visible',
        whiteSpace: 'normal',
        height: 'auto',
      },
    },
  });

  const { classes } = useStyles();

  const messages = risingRiskPageMessages.RISING_RISK_PAGE;
  const [selectedTab, setSelectedTab] = useState<'left' | 'right'>('left');
  const [loading, setLoading] = useState(true);
  const employerGroupStore = useStores().employerGroupStore;
  const risingRiskStore = useStores().risingRiskStore;
  const recommendedTabStore = useStores().recommendedTabStore;
  const [egNid, setEgNid] = useState<any>('');

  const navigate = useNavigate();

  const [
    risingRiskSummaryCards,
    setRisingRiskSummaryCards,
  ] = useState<RisingRiskSummaryCardsProps>({
    avgRiskScore: '',
    latentRisk: '',
    chronicDiagnosed: '',
    acuteDiagnosed: '',
  });
  const [diseasesData, setDiseasesData] = useState<DiseasesDataProps[]>([]);

  const diseasesColumns = [
    {
      Header: 'CONDITION NAME',
      Footer: 'CONDITION NAME',
      accessor: 'diseaseName',
      sticky: 'left',
      Cell: ({ value }: any) => (
        <>
          <div onClick={() => {}}>
            <DottedText text={value} />
          </div>
        </>
      ),
      id: 'diseaseName',
      showThisColumn: true,
      DropDownMenuItems: dropDownMenuItemsAlphabets,
    },
    // REQUIRED Not yet finalised
    // {
    //   Header: 'TOTAL COST ($)',
    //   Footer: 'TOTAL COST ($)',
    //   accessor: 'totalCost',
    //   sticky: 'left',
    //   cellType: 'none',
    //   icon: 'none',
    //   Cell: ({ value }: any) => <>{numFormatterWithSuffix(value, 1)}</>,
    //   id: 'totalCost',
    //   showThisColumn:true,
    //   DropDownMenuItems: dropDownMenuItemsValues,
    // },
    {
      Header: 'CURRENT MEMBERS',
      Footer: 'CURRENT MEMBERS',
      accessor: 'currentMembers',
      sticky: 'left',
      id: 'currentMembers',
      showThisColumn: true,
      DropDownMenuItems: dropDownMenuItemsValues,
    },
    {
      Header: 'RISK MEMBERS',
      Footer: 'RISK MEMBERS',
      accessor: 'riskMembers',
      sticky: 'left',
      id: 'riskMembers',
      showThisColumn: true,
      DropDownMenuItems: dropDownMenuItemsValues,
    },
  ];

  const handleRowOnClickTable = (row: any) => {
    recommendedTabStore?.setCategoryToBeCalled(row?.values?.diseaseName);
    risingRiskStore.setSelectedDiseaseName(row?.values?.diseaseName);
    risingRiskStore.setMembersUniqueList(
      diseasesData[row?.index]?.memberIdList,
    );
    navigate(RISING_RISK_DETAILS);
  };

  const getDiagnosedCount = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.latentRisk +
          properties.diagnosedCount,
        {
          riskProbability: 0.7,
          eg_nid: employerGroupStore.employerGroupResponseData.eg_nid,
        },
      )
      .then((response) => {
        const data = response?.data?.data;
        const tempRisingRiskSummaryCards: RisingRiskSummaryCardsProps = {
          avgRiskScore: '',
          latentRisk: '',
          chronicDiagnosed: '',
          acuteDiagnosed: '',
        };
        tempRisingRiskSummaryCards.latentRisk = data['latentRiskMembersCount'];
        tempRisingRiskSummaryCards.chronicDiagnosed =
          data['chronicDiagnosedCount'];
        setRisingRiskSummaryCards(tempRisingRiskSummaryCards);
      })
      .catch((ex) => {
        return ex;
      });
  };

  const getDiseasesCardData = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.latentRisk +
          properties.diseaseSummary,
        {
          riskProbability: 0.7,
          eg_nid: employerGroupStore.employerGroupResponseData.eg_nid,
        },
      )
      .then((response) => {
        const object1 = response?.data?.data;
        const tempDiseaseData = diseasesData;
        const diseasesNames = [];
        for (const key of Object.keys(object1)) {
          const diseaseDetails: DiseasesDataProps = {
            diseaseName: '',
            currentMembers: '',
            riskMembers: '',
            totalCost: '',
            memberIdList: [],
          };
          diseaseDetails.diseaseName = key;
          diseasesNames.push(key);
          const value = object1[key];
          diseaseDetails.currentMembers = value?.currentMembers;
          diseaseDetails.riskMembers = value?.riskMembers;
          diseaseDetails.totalCost = value?.totalCost;
          diseaseDetails.memberIdList = value?.memberIdList;
          tempDiseaseData.push(diseaseDetails);
        }
        setDiseasesData(tempDiseaseData);
        risingRiskStore.setListOfDiseases(diseasesNames);
      })
      .catch((ex) => {
        return ex;
      });
  };

  const getAllCharts = async () => {
    setLoading(true);
    Boolean(startDate !== '' && endDate !== '')
      ? await Promise.all([getDiseasesCardData(), getDiagnosedCount()]).then(
          () => {
            setLoading(false);
          },
        )
      : '';
    Boolean(startDate !== '' && endDate !== '') ? setLoading(false) : '';
  };

  useEffect(() => {
    Boolean(egNid !== '') ? getAllCharts() : '';
  }, [egNid]);

  useEffect(() => {
    setEgNid(employerGroupStore.employerGroupResponseData.eg_nid);
  }, []);

  const [loader] = useState<any>(<Loader />);

  const [columnId, setColumnId] = useState<string>();

  const [
    showSortingAndFiltering,
    setShowSortingAndFiltering,
  ] = useState<boolean>(true);

  const handleSort = (event: any, column: any, item: any) => {
    const sortOrder = item.actionType === 'ascendingSorting' ? 1 : -1;
    const sortColumnName = column.id;
    if (!sortColumnName) {
      return;
    }

    const newList = diseasesData
      ? [...diseasesData].sort((a: any, b: any) => {
          if (a[sortColumnName] < b[sortColumnName]) {
            return -1 * sortOrder;
          } else if (a[sortColumnName] > b[sortColumnName]) {
            return 1 * sortOrder;
          }
          return 0;
        })
      : [];

    setDiseasesData(newList);
  };

  return loading ? (
    loader
  ) : (
    <Grid container className={classes.root}>
      <Grid
        style={{ display: 'flex', marginTop: '19px', marginBottom: '32px' }}
      >
        {/*     // AVG_RISK is not yet finalised
        <Grid style={{ marginRight: '24px' }}>
          <OverviewCard
            title={messages.SUMMARY_CARDS.AVG_RISK}
            value={'$' + risingRiskSummaryCards.avgRiskScore}
            rootStyles={{
              width: '176px',
            }}
          />
        </Grid> */}
        <Grid style={{ marginRight: '24px' }}>
          <OverviewCard
            title={messages.SUMMARY_CARDS.LATENT_RISK}
            value={valueWithText(risingRiskSummaryCards.latentRisk, 'Members')}
            rootStyles={{
              width: '200px',
              height: '100px',
            }}
            // labelsMinWidth="72px"
          />
        </Grid>
        <Grid style={{ marginRight: '24px' }}>
          <OverviewCard
            title={messages.SUMMARY_CARDS.CHRONIC_DIAGNOSED}
            value={valueWithText(
              risingRiskSummaryCards.chronicDiagnosed,
              'Members',
            )}
            rootStyles={{
              width: '220px',
              height: '100px',
            }}
          />
        </Grid>
        {/* ACUTE DIAGNOSED Not Finalised
        <Grid style={{ marginRight: '24px' }}>
          <OverviewCard
            title={messages.SUMMARY_CARDS.ACUTE_DIAGNOSED}
            value={valueWithText(
              risingRiskSummaryCards.acuteDiagnosed,
              'Members',
            )}
            rootStyles={{
              width: '176px',
            }}
          />
        </Grid> */}
      </Grid>
      <Grid>
        <Grid container direction="row" className={classes.diseasesHeadingGrid}>
          <Grid
            size={{
              xs: 10,
              sm: 10,
              lg: 10,
            }}
            sx={{
              alignItems: 'flex-start',
            }}
          >
            <Typography
              fontSize="18px"
              fontType="inter"
              lineHeight="26px"
              textColor={'#2E2F33'}
              // margin="28px 0px 0px 0px"
            >
              {messages.DISEASES}
            </Typography>
          </Grid>
          <Grid
            container
            direction="row"
            size={{
              xs: 2,
              sm: 2,
              lg: 2,
            }}
            sx={{
              alignContent: 'flex-end',
            }}
          >
            <ToggleGroup
              leftValue="Card View"
              leftIcon={<CardViewSvg />}
              rightValue="Table View"
              rightIcon={<TableViewSvg />}
              setSelectedToggle={setSelectedTab}
              leftIconActiveStyles={{
                color: '#475CB0',
                backgroundColor: '#475CB0',
              }}
              rightIconActiveStyles={{
                color: '#475CB0',
                backgroundColor: '#475CB0',
              }}
            />
          </Grid>
        </Grid>
        <Grid container direction="row">
          {selectedTab === 'left' ? (
            diseasesData?.map((disease, index: number) => {
              return (
                <Grid
                  style={{ marginRight: '20px', marginBottom: '20px' }}
                  key={index}
                >
                  <RiskOverviewCard
                    // icon={
                    //   <DiseaseSvg style={{ width: '24px', height: '24px' }} />
                    // }
                    width="376px"
                    height="184px"
                    isRisingRiskPage={true}
                    title={
                      <Typography
                        fontSize="14px"
                        fontType="semiBold"
                        lineHeight="16.94px"
                        textColor={patientAccessTheme.palette.text.highemphasis}
                        className={classes.ellipses}
                      >
                        {disease?.diseaseName}
                      </Typography>
                    }
                    subTitle={
                      <Typography
                        fontSize="14px"
                        lineHeight="16.94px"
                        fontType="regular"
                        textColor={
                          patientAccessTheme.palette.text.mediumemphasis
                        }
                      >
                        {messages.DISEASES_CARD_SUBTITLE}
                      </Typography>
                    }
                    labels={['Current members', 'Risk members']}
                    values={[disease?.currentMembers, disease?.riskMembers]}
                    handleOnClick={() => {
                      recommendedTabStore?.setCategoryToBeCalled(
                        disease?.diseaseName,
                      );
                      risingRiskStore.setSelectedDiseaseName(
                        disease?.diseaseName,
                      );
                      risingRiskStore.setMembersUniqueList(
                        disease?.memberIdList,
                      );
                      navigate(RISING_RISK_DETAILS);
                    }}
                  />
                </Grid>
              );
            })
          ) : (
            <Table
              onHoverRowData={() => {}}
              handleRowOnClick={(row: any) => {
                handleRowOnClickTable(row);
              }}
              classNameForTableHeaderRowTop={classes.diseasesTableHeader}
              columns={diseasesColumns}
              data={diseasesData}
              controlledPageIndex={0}
              isManualPagination={true}
              overallGridStyles={classes.overallGridStyles}
              isRisingRiskPage={false}
              columnId={columnId}
              handleMouseEnterHeader={(column: any) =>
                handleHeaderMouseEnter(column, setColumnId)
              }
              handleMouseLeaveHeader={() => handleHeaderMouseLeave(setColumnId)}
              showSortingAndFiltering={showSortingAndFiltering}
              setShowSortingAndFiltering={setShowSortingAndFiltering}
              showOnHoverHeader={true}
              handleMenuItem={handleSort}
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
export default RisingRiskOverviewTab;
